import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cases as casesApi, personas as personasApi, users as usersApi } from "../api/endpoints";
import { useAuth } from "../auth/AuthContext";
import Spinner from "../components/Spinner";
import Modal from "../components/Modal";
import { TipoPill, TIPO_LABEL } from "../components/Pills";
import { useToast } from "../components/Toast";
import { useDebounced } from "../components/useDebounced";
import LitisoftFields from "../components/LitisoftFields";
import { TERMINO_OPTIONS, formatTermino } from "../utils/termino";
import { PopoverSelect, PopoverCombobox } from "../components/Popover";
import { CASE_TAXONOMY, deriveFalloFields } from "../utils/case_taxonomy";

const TIPOS = ["tutela", "fallo", "requerimiento", "desacato", "sancion", "consulta"];
const ENTIDAD_NA = ["FIDUPREVISORA", "FOMAG", "INPEC", "EPS", "OTRA"];

const TIPO_FALLO_OPTS = [
  ["FAVORABLE_1A",     "Favorable 1ª instancia"],
  ["DESFAVORABLE_1A",  "Desfavorable 1ª instancia"],
  ["FAVORABLE_2A",     "Favorable 2ª instancia"],
  ["DESFAVORABLE_2A",  "Desfavorable 2ª instancia"],
  ["NO_PEDIR_INSUMO",  "No pedir insumo"],
];

const CIERRE_DESACATO_OPTS = [
  "ABSTIENE_ABRIR_DESACATO","SE_ABSTIENE_DE_CONTINUAR","ABSTIENE_DE_SANCIONAR","ARCHIVA",
  "DECLARA_TERMINADO","ABSTIENE_DE_APERTURAR","DECLARA_IMPROCEDENTE","NIEGA_LA_PROCEDENCIA",
  "NO_DA_APERTURA","DESVINCULA","DESISTIMIENTO","NULIDAD",
];

const ETAPA_PROCESAL_OPTS = [
  "SE_ABSTIENE_DE_SANCIONAR","OFICIOS_DE_ARRESTO_Y_O_COBRO","NO_VINCULA","REVOCA_EN_CONSULTA",
  "EN_CONSULTA","ESTARSE_A_LO_RESUELTO_REVOCA","NULIDAD_REINICIA_TRAMITE","INAPLICACION_ARCHIVO",
  "SANCION","ARCHIVO","CONFIRMA_EN_CONSULTA","SE_ABSTIENE_DE_SANCIONAR_MAS_SANCION",
];

// feature/enum-display-labels — readable Spanish labels for the sanción/desacato
// enum dropdowns (keys stay untouched — only the display text changes). Falls
// back to the de-underscored key if a value is ever added without a label.
const CIERRE_DESACATO_LABELS = {
  ABSTIENE_ABRIR_DESACATO: "Se abstiene de abrir desacato",
  SE_ABSTIENE_DE_CONTINUAR: "Se abstiene de continuar",
  ABSTIENE_DE_SANCIONAR: "Se abstiene de sancionar",
  ARCHIVA: "Archiva",
  DECLARA_TERMINADO: "Declara terminado",
  ABSTIENE_DE_APERTURAR: "Se abstiene de aperturar",
  DECLARA_IMPROCEDENTE: "Declara improcedente",
  NIEGA_LA_PROCEDENCIA: "Niega la procedencia",
  NO_DA_APERTURA: "No da apertura",
  DESVINCULA: "Desvincula",
  DESISTIMIENTO: "Desistimiento",
  NULIDAD: "Nulidad",
};
const ETAPA_PROCESAL_LABELS = {
  SE_ABSTIENE_DE_SANCIONAR: "Se abstiene de sancionar",
  OFICIOS_DE_ARRESTO_Y_O_COBRO: "Oficios de arresto y/o cobro",
  NO_VINCULA: "No vincula",
  REVOCA_EN_CONSULTA: "Revoca en consulta",
  EN_CONSULTA: "En consulta",
  ESTARSE_A_LO_RESUELTO_REVOCA: "Estarse a lo resuelto / revoca",
  NULIDAD_REINICIA_TRAMITE: "Nulidad / reinicia trámite",
  INAPLICACION_ARCHIVO: "Inaplicación / archivo",
  SANCION: "Sanción",
  ARCHIVO: "Archivo",
  CONFIRMA_EN_CONSULTA: "Confirma en consulta",
  SE_ABSTIENE_DE_SANCIONAR_MAS_SANCION: "Se abstiene de sancionar (más sanción)",
};
const enumLabel = (map, o) => map[o] || o.replaceAll("_", " ");

export default function NewCase() {
  const nav = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [tipo, setTipo] = useState(null);

  // feature/consulta-scope — hide "consulta" from reparto-only users. Consultas don't
  // enter at intake; they're created from within the sanción workflow when
  // Fiorella decides a sanción case is actually a consulta. Admin/directora
  // (and any other role) keep full access.
  const userRoles = user?.roles && user.roles.length ? user.roles : (user?.role ? [user.role] : []);
  const hideConsulta = userRoles.includes("reparto")
    && !userRoles.includes("admin") && !userRoles.includes("directora");
  const visibleTipos = hideConsulta ? TIPOS.filter(t => t !== "consulta") : TIPOS;

  return (
    <div className="col" style={{ gap: 16 }}>
      <h1 style={{ margin: 0 }}>Nueva acción</h1>
      <div className="muted" style={{ marginTop: -8 }}>Seleccione el tipo de caso y complete los datos. La fecha de ingreso se registra con la hora del servidor.</div>

      <div style={{ display: "grid", gridTemplateColumns: `repeat(${visibleTipos.length}, minmax(0,1fr))`, gap: 12 }}>
        {visibleTipos.map(t => (
          <div
            key={t}
            className={"card clickable" + (tipo === t ? " selected" : "")}
            onClick={() => setTipo(t)}
            style={tipo === t ? { borderColor: "var(--accent-mint)", boxShadow: "var(--shadow-md)" } : {}}
          >
            <TipoPill tipo={t} />
            <div style={{ fontSize: 16, fontWeight: 600, marginTop: 8, color: "var(--text-primary)" }}>{(TIPO_LABEL[t] || CASE_TAXONOMY[t]?.label || t)}</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              {({
                tutela: "DP/MP, N/A, abogado, término",
                fallo: "Tipo de fallo, Molly decide impugnación",
                requerimiento: "Abogado define término al recibirlo",
                desacato: "Cierre con tipificación",
                sancion: "Apertura/Cierre + etapa procesal",
                consulta: "Consulta jurídica sin actuación específica",
              })[t]}
            </div>
          </div>
        ))}
      </div>

      {tipo && (
        <CaseForm
          tipo={tipo}
          onCancel={() => setTipo(null)}
          onCreated={(c) => {
            toast.push(`Caso ${c.case_number} creado`, "success");
            nav(`/cases?open=${c.id}`);
          }}
        />
      )}
    </div>
  );
}

function CaseForm({ tipo, onCancel, onCreated }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  // Persona auto-suggest
  const [cedula, setCedula] = useState("");
  const [nombre, setNombre] = useState("");
  const [persona_id, setPersonaId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const cedulaDebounced = useDebounced(cedula, 250);
  useEffect(() => {
    if (!cedulaDebounced) { setSuggestions([]); return; }
    if (persona_id) return;
    personasApi.search(cedulaDebounced).then(r => setSuggestions(r.data)).catch(() => setSuggestions([]));
  }, [cedulaDebounced, persona_id]);

  // Base fields
  const [radicado, setRadicado] = useState("");
  const [juzgado, setJuzgado] = useState("");
  const [terminoHoras, setTerminoHoras] = useState("");
  const [abogadoId, setAbogadoId] = useState("");
  // feature/intake-defaults — default-checked for ALL case types (was tutela-only).
  // Reparto can uncheck if certain a case doesn't need insumos; the abogado
  // can also flip from case detail later (feature/intake-checkbox toggle preserved).
  // feature/fallos-default-to-molly (Angie correction 2026-05-06,
  // superseding 58c). Fallos default CHECKED again — Angie's actual workflow
  // is "casi todos requieren insumos; solo desmarco para favorables claros."
  // 58c had it inverted. Default re-flipped to true; 58d also force-locks the
  // checkbox CHECKED for desfavorable sub_tipos (server enforces too as
  // defense-in-depth). Either way Molly is the abogado_asignado_id from
  // creation (parallel visibility, "de la mano con insumos").
  const [requiereInsumo, setRequiereInsumo] = useState(true);
  useEffect(() => { setRequiereInsumo(true); }, [tipo]);
  // feature/accionado-locked — accionado is locked to "Previsora S.A. compañía de seguros"
  // at every layer (DB CHECK, backend force, UI static). No state needed.
  // feature/tipo-taxonomy — taxonomy fields. Sub-tipo is required when the tipo has any.
  const [subTipo, setSubTipo] = useState("");
  const [noAplica, setNoAplica] = useState(false);
  useEffect(() => { setSubTipo(""); setNoAplica(false); }, [tipo]);
  // feature/fallos-default-to-molly. Force-lock requiereInsumo=true
  // when Angie picks a desfavorable sub-tipo. The visible checkbox is also
  // disabled below; backend force-overrides too (defense-in-depth).
  const isDesfavorableFallo = tipo === "fallo"
    && (subTipo === "primera_desfavorable" || subTipo === "segunda_desfavorable");
  useEffect(() => {
    if (isDesfavorableFallo) setRequiereInsumo(true);
  }, [isDesfavorableFallo]);
  // feature/auditoria-diaria — actuaciones urgentes (manual flag at intake; backend also
  // auto-flags when termino_horas <= 8).
  const [urgente, setUrgente] = useState(false);
  const [observaciones, setObservaciones] = useState("");
  const [flag_dp, setDp] = useState(false);
  const [flag_mp, setMp] = useState(false);
  // feature/nulidad-bandera — sprint86. Re-intake after a decreed nulidad.
  const [flag_vn, setVn] = useState(false);
  const [flag_na, setNa] = useState(false);
  const [entidad_na, setEntidad] = useState("");
  const [forzar, setForzar] = useState(false);
  const [autorizadoPor, setAutorizadoPor] = useState("");

  // Type-specific
  const [tipoFallo, setTipoFallo] = useState("");
  const [cierre, setCierre] = useState("");
  const [sancionTipo, setSancionTipo] = useState("");
  const [etapa, setEtapa] = useState("");
  const [fechaCierre, setFechaCierre] = useState("");
  // feature/sancion-no-previsora.
  const [sancionNoPrevisora, setSancionNoPrevisora] = useState(false);

  // feature/litisoft-export LITISOFT optional fields
  const [litisoft, setLitisoft] = useState({});

  // Users
  const [allUsers, setAllUsers] = useState([]);
  useEffect(() => { usersApi.list().then(r => setAllUsers(r.data)); }, []);

  // Duplicate detection. Sprint 63 — re-runs when tipo changes too,
  // because the strict-match qualifier on the backend now depends on
  // tipo. Same radicado + DIFFERENT tipo is a legitimate cross-actuación
  // (Tutela → Fallo on the same proceso) and surfaces only as a warning.
  const radicadoDeb = useDebounced(radicado, 400);
  const accionanteDeb = useDebounced(nombre, 400);
  const [dup, setDup] = useState({ strict: [], warning: [] });
  useEffect(() => {
    if (!radicadoDeb && !accionanteDeb) { setDup({ strict: [], warning: [] }); return; }
    casesApi.checkDuplicates(radicadoDeb, accionanteDeb, tipo)
      .then(r => setDup(r.data)).catch(() => {});
  }, [radicadoDeb, accionanteDeb, tipo]);

  const [strictModal, setStrictModal] = useState(false);
  useEffect(() => {
    if (dup.strict?.length) setStrictModal(true);
  }, [dup.strict?.length]);

  function pickPersona(p) {
    setPersonaId(p.id);
    setCedula(p.cedula || "");
    setNombre(p.nombre);
    setSuggestions([]);
  }
  function clearPersona() {
    setPersonaId(null);
    setSuggestions([]);
  }

  async function submit(e) {
    e.preventDefault();
    if (dup.strict?.length) { setStrictModal(true); return; }
    // feature/intake-swap-guard — C1.5. Closes the autofill / paste-into-
    // wrong-field hole that produced 6 swapped rows in the May 5–8 batch
    // (cases 241/261/455/583/666/674). A Colombian court radicado is
    // by definition numeric (short form 2026-XXXXX, long form 23
    // digits) — zero digits = swap with the accionante name.
    if (!/\d/.test(radicado.trim())) {
      toast.push("El radicado debe contener números. Verifique que no haya escrito el nombre del accionante en este campo.", "error");
      return;
    }

    const payload = {
      tipo,
      radicado: radicado.trim(),
      juzgado:  juzgado.trim() || null,
      observaciones: observaciones || null,
      flag_dp, flag_mp, flag_na,
      flag_vuelve_despues_de_nulidad: flag_vn,
      entidad_na: flag_na ? (entidad_na || null) : null,
      forzar_insumo: forzar,
      autorizado_por_id: forzar ? Number(autorizadoPor) || null : null,
      // feature/sancion-hide-requiere-insumo. Sanciones don't go
      // through the insumos pipeline, so force the flag off and skip the
      // visible toggle. Backend defaults already pin sancion → FALSE.
      requiere_insumo: tipo === "sancion" ? false : requiereInsumo,
      sancion_no_previsora: tipo === "sancion" ? !!sancionNoPrevisora : false,
      // feature/accionado-locked — accionado is locked at the DB+backend; no need to send.
      // feature/sancion-no-subtipo. Sanciones never carry sub_tipo
      // (the "Tipo de sanción" field below is the canonical taxonomy).
      sub_tipo: tipo === "sancion" ? null : (subTipo || null),
      no_aplica: tipo === "tutela" ? !!noAplica : false,
      urgente: !!urgente,
    };
    // feature/tipo-taxonomy — fallos derive instancia/resultado from the chosen sub-tipo.
    if (tipo === "fallo" && subTipo) {
      const f = deriveFalloFields(subTipo);
      payload.instancia = f.instancia;
      payload.resultado = f.resultado;
    }
    if (persona_id) payload.persona_id = persona_id;
    else payload.persona = { cedula: cedula || null, nombre: nombre.trim() };

    Object.assign(payload, litisoft);

    if (tipo === "tutela") {
      payload.termino_horas = Number(terminoHoras);
      payload.abogado_asignado_id = Number(abogadoId);
    }
    if (tipo === "fallo") {
      // feature/fallo-dropdown-dedup — derive the legacy tipo_fallo enum from the unified
      // sub_tipo selection so case_type_data + the Decisión tab keep working.
      // Sprint 54 — added 'nulidad' → 'NULIDAD' so Angie can create fallo nulidad cases.
      const map = {
        primera_favorable:    "FAVORABLE_1A",
        primera_desfavorable: "DESFAVORABLE_1A",
        segunda_favorable:    "FAVORABLE_2A",
        segunda_desfavorable: "DESFAVORABLE_2A",
        nulidad:              "NULIDAD",
      };
      payload.tipo_fallo = map[subTipo] || tipoFallo || null;
    }
    if (tipo === "requerimiento") {
      payload.abogado_asignado_id = abogadoId ? Number(abogadoId) : null;
    }
    if (tipo === "desacato") {
      payload.abogado_asignado_id = abogadoId ? Number(abogadoId) : null;
      payload.cierre_desacato = cierre || null;
    }
    if (tipo === "sancion") {
      payload.abogado_asignado_id = abogadoId ? Number(abogadoId) : null;
      payload.sancion_tipo = sancionTipo;
      payload.etapa_procesal = etapa;
      if (sancionTipo === "CIERRE") payload.fecha_cierre = fechaCierre || null;
    }

    setBusy(true);
    try {
      const r = await casesApi.create(payload);
      onCreated(r.data);
    } catch (err) {
      const e = err?.response?.data;
      // Sprint 63 — server returns error: "radicado_action_duplicate" with
      // an `existing` row when same radicado + same tipo collides.
      if (err?.response?.status === 409 && e?.existing) {
        setDup(prev => ({ ...prev, strict: [e.existing] }));
        setStrictModal(true);
      } else {
        // Prefer the human message (Sprint 60+ backends include both
        // `message` and `error` code; older endpoints just have `error`).
        toast.push(e?.message || e?.error || "Error al crear", "error");
      }
    } finally { setBusy(false); }
  }

  // feature/single-role-audit — FALSE POSITIVE: list filter on PRIMARY
  // role for the assignment dropdown.
  const abogados = useMemo(() => allUsers.filter(u => u.role === "abogado" && u.active), [allUsers]);

  return (
    <form className="card" onSubmit={submit}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <h2 style={{ margin: 0 }}>Datos {TIPO_LABEL[tipo]}</h2>
        <TipoPill tipo={tipo} />
        <button type="button" className="ghost" style={{ marginLeft: "auto" }} onClick={onCancel}>Cambiar tipo</button>
      </div>
      <div className="rule" />

      <div className="grid-2">
        <div style={{ position: "relative" }}>
          <label>Cédula del accionante (opcional)</label>
          {/* feature/intake-swap-guard — C1.5. autoComplete="off" disables
              browser / password-manager heuristics that cross-wired
              accionante names into the radicado field. */}
          <input value={cedula} onChange={(e) => { clearPersona(); setCedula(e.target.value); }} placeholder="Sin puntos" autoComplete="off" name="cedula" />
          {suggestions.length > 0 && !persona_id && (
            <div style={{ position: "absolute", left: 0, right: 0, top: 64, background: "var(--bg-card)", border: "1px solid var(--border-hairline)", borderRadius: 8, boxShadow: "var(--shadow-md)", zIndex: 5 }}>
              {suggestions.map(s => (
                <div key={s.id} style={{ padding: "8px 10px", cursor: "pointer", borderBottom: "1px solid var(--border-hairline)" }}
                     onMouseEnter={(e) => e.currentTarget.style.background = "var(--bg-subtle)"}
                     onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                     onClick={() => pickPersona(s)}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>{s.nombre}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>CC {s.cedula || "—"}</div>
                </div>
              ))}
            </div>
          )}
          {persona_id && <div style={{ fontSize: 11, color: "var(--accent-mint)", marginTop: 4 }}>Persona existente seleccionada</div>}
        </div>

        <div>
          <label>Nombre del accionante {persona_id && <span className="muted">(autocompletado)</span>}</label>
          <input value={nombre} onChange={(e) => { clearPersona(); setNombre(e.target.value); }} required autoComplete="off" name="accionante_nombre" />
          {!!dup.warning?.length && (
            <div className="warn-inline" style={{ marginTop: 6 }}>
              ⚠ Este accionante ya tiene {dup.warning.length} caso(s) abierto(s). Verifique antes de continuar.
            </div>
          )}
        </div>

        <div>
          <label>Radicado</label>
          <input value={radicado} onChange={(e) => setRadicado(e.target.value)} required autoComplete="off" name="radicado" inputMode="text" />
          {!!dup.strict?.length && (
            <div className="warn-inline" style={{ marginTop: 6, background: "var(--color-danger-bg)", borderColor: "var(--color-danger)", color: "var(--color-danger)" }}>
              ⛔ Ya existe un caso con este radicado y la misma actuación ({TIPO_LABEL[dup.strict[0]?.tipo] || dup.strict[0]?.tipo} · {dup.strict[0]?.case_number}). No puede crear un duplicado.
            </div>
          )}
          {/* Sprint 63 — cross-actuación info chip. Same radicado but a
              DIFFERENT actuación type already exists on the proceso. This
              is the legitimate Tutela → Fallo → Impugnación pattern, NOT
              a duplicate. Informational, doesn't block submit. */}
          {!dup.strict?.length && (dup.warning || []).filter((w) => w.reason === "cross_actuacion").map((w) => (
            <div key={`cross-${w.id}`} className="warn-inline" style={{
              marginTop: 6,
              background: "var(--color-info-bg)",
              borderColor: "var(--accent-mint)",
              color: "var(--accent-mint)",
            }}>
              ℹ️ Este radicado ya tiene una actuación registrada: {TIPO_LABEL[w.tipo] || w.tipo} ({w.case_number}). Puede registrar la nueva actuación sin problema — quedan vinculadas al mismo proceso.
            </div>
          ))}
        </div>

        <div>
          <label>Juzgado (opcional)</label>
          <input value={juzgado} onChange={(e) => setJuzgado(e.target.value)} autoComplete="off" name="juzgado" />
        </div>

        <div style={{ gridColumn: "span 2" }}>
          <label>Accionado</label>
          <div style={{
            padding: "8px 12px",
            background: "var(--color-hover-bg)",
            border: "1px solid var(--border-hairline)",
            borderRadius: 8,
            color: "var(--text-primary)",
            fontSize: 13,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}>
            <span>Previsora S.A. compañía de seguros</span>
            <span className="muted" style={{ fontSize: 11, fontStyle: "italic" }}>Valor fijo del sistema</span>
          </div>
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            El accionado siempre es Previsora S.A. compañía de seguros.
          </div>
        </div>

        {/* feature/tipo-taxonomy sub-tipo, relabeled in feature/fallo-dropdown-dedup for clarity (was "Sub-tipo").
            For fallo, this is the single source of truth — the legacy
            tipo_fallo dropdown was removed. Other tipos use it as their
            hierarchical sub-type.
            feature/sancion-no-subtipo. Sanciones use the
            "Tipo de sanción" field below for taxonomy; this generic
            sub-tipo dropdown duplicated those options. Hide it for
            tipo='sancion'; existing sancion rows with a sub_tipo value
            still work (column is nullable, just no longer rendered at
            intake/edit). */}
        {tipo !== "sancion" && (CASE_TAXONOMY[tipo]?.sub_tipos || []).length > 0 && (
          <div style={{ gridColumn: "span 2" }}>
            <label>{tipo === "fallo" ? "Tipo de fallo" : "Sub-tipo"}</label>
            <PopoverSelect
              value={subTipo || null}
              placeholder="— elegir —"
              options={CASE_TAXONOMY[tipo].sub_tipos.map(s => ({ value: s.value, label: s.label }))}
              onChange={(v) => setSubTipo(v || "")}
            />
          </div>
        )}

        {/* feature/tipo-taxonomy — Tutela "no aplica a Previsora" flag. */}
        {tipo === "tutela" && (
          <div style={{ gridColumn: "span 2" }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 0, textTransform: "none", letterSpacing: 0, fontSize: 13, color: "var(--text-body)" }}>
              <input type="checkbox" checked={noAplica} onChange={(e) => setNoAplica(e.target.checked)} />
              Esta tutela no aplica a Previsora (N/A)
            </label>
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              El caso se crea pero salta el enrutamiento automático a Insumos. Reparto lo gestiona manualmente.
            </div>
          </div>
        )}

        <div style={{ gridColumn: "span 2" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 0, textTransform: "none", letterSpacing: 0, fontSize: 13, color: "var(--text-body)" }}>
            <input type="checkbox" checked={urgente} onChange={(e) => setUrgente(e.target.checked)} />
            🚩 Marcar como urgente
          </label>
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            Casos con plazo crítico o complejidad alta. Se marca automáticamente cuando el término ≤ 8 horas.
          </div>
        </div>

        {/* feature/sancion-hide-requiere-insumo. Sanciones don't
            run through the insumos pipeline (Fiorella's workflow).
            feature/fallos-default-to-molly. Fallos
            relabel the checkbox to "Solicitar insumos antes de pasar a
            Molly". Pre-checked. Locked checked + helper indicator when
            sub_tipo is a desfavorable (Angie's directive: desfavorables
            siempre piden insumos). Server enforces too. */}
        {tipo !== "sancion" && (
          <div style={{ gridColumn: "span 2" }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 0, textTransform: "none", letterSpacing: 0, fontSize: 13, color: "var(--text-body)", opacity: isDesfavorableFallo ? 0.85 : 1 }}>
              <input type="checkbox"
                     checked={isDesfavorableFallo ? true : requiereInsumo}
                     disabled={isDesfavorableFallo}
                     onChange={(e) => setRequiereInsumo(e.target.checked)} />
              {tipo === "fallo"
                ? "Solicitar insumos a Previsora antes de pasar a Molly"
                : "¿Requiere insumo?"}
              {isDesfavorableFallo && (
                <span style={{ fontSize: 11, color: "var(--color-warning)", marginLeft: 6, fontWeight: 500 }}>
                  🔒 obligatorio para desfavorables
                </span>
              )}
            </label>
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              {tipo === "fallo"
                ? "Marcado por defecto. Desmárquela solo si el fallo es favorable y no necesita insumos. Para desfavorables el sistema siempre solicita insumos automáticamente."
                : "Casi todos los casos requieren insumo. Por defecto está marcada — desmárquela solo si tiene certeza de que el caso no necesita información adicional de Previsora."}
            </div>
          </div>
        )}
      </div>

      {/* Type-specific fields */}
      {tipo === "tutela" && (
        <>
          {/* Hotfix — Reparto no longer chooses requiere_insumo at intake.
              Backend defaults: tutela=TRUE, others=FALSE. The abogado can
              flip this from the case detail General tab if the case actually
              doesn't need insumos. */}
          <div className="grid-2" style={{ marginTop: 12 }}>
            <div>
              <label>Término</label>
              <PopoverSelect
                value={terminoHoras ? Number(terminoHoras) : null}
                placeholder="— elegir —"
                options={TERMINO_OPTIONS.map(h => ({ value: h, label: formatTermino(h) }))}
                onChange={(v) => setTerminoHoras(v ?? "")}
              />
            </div>
            <div>
              <label>Abogado asignado</label>
              <PopoverCombobox
                value={abogadoId ? Number(abogadoId) : null}
                label={abogados.find(u => String(u.id) === String(abogadoId))?.name || null}
                placeholder="— elegir abogado —"
                options={abogados.map(u => ({ value: u.id, label: u.name }))}
                onChange={(id) => setAbogadoId(id ?? "")}
              />
            </div>
          </div>
          <Flags state={{ flag_dp, setDp, flag_mp, setMp, flag_vn, setVn, flag_na, setNa, entidad_na, setEntidad, forzar, setForzar, autorizadoPor, setAutorizadoPor, allUsers }} />
        </>
      )}

      {tipo === "fallo" && (
        <div style={{ marginTop: 12 }}>
          {/* feature/fallo-dropdown-dedup — fallo type is captured by the unified hierarchical
              dropdown above ("Tipo de fallo"). The legacy enum select was
              removed because it duplicated the same information. */}
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>El fallo se asigna automáticamente a Dra. Molly desde la creación. {requiereInsumo ? "Como está marcada «Solicitar insumos», también entra al flujo de Insumos — Molly lo verá «de la mano con insumos» (en paralelo)." : "Como desmarcó «Solicitar insumos», llega directo a su cola de revisión sin pasar por Insumos."}</div>
          <Flags state={{ flag_dp, setDp, flag_mp, setMp, flag_vn, setVn, flag_na, setNa, entidad_na, setEntidad, forzar, setForzar, autorizadoPor, setAutorizadoPor, allUsers }} />
        </div>
      )}

      {tipo === "requerimiento" && (
        <>
          <div className="grid-2" style={{ marginTop: 12 }}>
            <div>
              <label>Abogado asignado (opcional)</label>
              <select value={abogadoId} onChange={(e) => setAbogadoId(e.target.value)}>
                <option value="">— sin asignar —</option>
                {abogados.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>
          <Flags state={{ flag_dp, setDp, flag_mp, setMp, flag_vn, setVn, flag_na, setNa, entidad_na, setEntidad, forzar, setForzar, autorizadoPor, setAutorizadoPor, allUsers }} />
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Reparto no fija el término — el abogado lo establecerá tras leer el auto.</div>
        </>
      )}

      {tipo === "desacato" && (
        <>
          <div className="grid-2" style={{ marginTop: 12 }}>
            <div>
              <label>Cierre <span className="muted" style={{ fontSize: 11, fontWeight: 400 }}>(opcional)</span></label>
              <select value={cierre} onChange={(e) => setCierre(e.target.value)}>
                <option value="">— elegir (opcional) —</option>
                {CIERRE_DESACATO_OPTS.map(o => <option key={o} value={o}>{enumLabel(CIERRE_DESACATO_LABELS, o)}</option>)}
              </select>
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                Solo aplica cuando el desacato ya tiene un fallo de cierre. Para aperturas, déjelo vacío.
              </div>
            </div>
            <div>
              <label>Abogado asignado (opcional)</label>
              <select value={abogadoId} onChange={(e) => setAbogadoId(e.target.value)}>
                <option value="">— sin asignar —</option>
                {abogados.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>
          <Flags state={{ flag_dp, setDp, flag_mp, setMp, flag_vn, setVn, flag_na, setNa, entidad_na, setEntidad, forzar, setForzar, autorizadoPor, setAutorizadoPor, allUsers }} />
        </>
      )}

      {tipo === "sancion" && (
        <>
          {/* feature/sancion-no-previsora. Sanciones whose
              demanded entity is NUEVA EPS (or any non-Previsora) get tagged
              so they don't show up in Fiorella's vencidas dashboard. */}
          <div style={{ marginTop: 12 }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 0, textTransform: "none", letterSpacing: 0, fontSize: 13, color: "var(--text-body)" }}>
              <input type="checkbox" checked={sancionNoPrevisora}
                     onChange={(e) => setSancionNoPrevisora(e.target.checked)} />
              Esta sanción NO es para Previsora (ej: NUEVA EPS)
            </label>
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              Si se marca, la sanción no aparecerá en cola de vencidas para Fiorella.
            </div>
          </div>
          <div className="grid-2" style={{ marginTop: 12 }}>
            <div>
              <label>Tipo de sanción</label>
              <PopoverSelect
                value={sancionTipo}
                placeholder="— elegir —"
                options={[
                  { value: "APERTURA",   label: "Apertura" },
                  { value: "CIERRE",     label: "Cierre" },
                  { value: "no_vincula", label: "No vincula a Previsora" },
                ]}
                onChange={(v) => setSancionTipo(v || "")}
              />
              {sancionTipo === "no_vincula" && (
                <div className="warn-inline" style={{ marginTop: 6 }}>
                  Este caso será marcado como no vinculante a Previsora. Confirme con la Dra. Fiorella antes de proceder.
                </div>
              )}
            </div>
            <div>
              <label>Etapa procesal</label>
              <PopoverSelect
                value={etapa}
                placeholder="— elegir —"
                searchable
                options={ETAPA_PROCESAL_OPTS.map(o => ({ value: o, label: enumLabel(ETAPA_PROCESAL_LABELS, o) }))}
                onChange={(v) => setEtapa(v || "")}
              />
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                Opcional — puede completar este campo más tarde.
              </div>
            </div>
            {sancionTipo === "CIERRE" && (
              <div>
                <label>Fecha de cierre</label>
                <input type="date" value={fechaCierre} onChange={(e) => setFechaCierre(e.target.value)} required />
              </div>
            )}
            <div>
              <label>Abogado asignado (opcional)</label>
              <select value={abogadoId} onChange={(e) => setAbogadoId(e.target.value)}>
                <option value="">— sin asignar —</option>
                {abogados.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>
          <Flags state={{ flag_dp, setDp, flag_mp, setMp, flag_vn, setVn, flag_na, setNa, entidad_na, setEntidad, forzar, setForzar, autorizadoPor, setAutorizadoPor, allUsers }} />
        </>
      )}

      <div style={{ marginTop: 12 }}>
        <label>Observaciones</label>
        <textarea value={observaciones} onChange={(e) => setObservaciones(e.target.value)} placeholder="Notas internas, contexto, prioridad…" />
      </div>

      <div style={{ marginTop: 12 }}>
        <LitisoftFields values={litisoft} onChange={setLitisoft} />
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
        <button type="button" className="secondary" onClick={onCancel}>Cancelar</button>
        <button type="submit" disabled={busy || !!dup.strict?.length}>
          {busy && <Spinner color="currentColor" />} Crear caso
        </button>
      </div>

      <Modal
        open={strictModal}
        title="Radicado duplicado"
        onClose={() => setStrictModal(false)}
        footer={
          <>
            <button className="secondary" onClick={() => setStrictModal(false)}>Cancelar</button>
            {dup.strict[0] && (
              <button onClick={() => { window.location.href = `/cases?open=${dup.strict[0].id}`; }}>Ver caso existente</button>
            )}
          </>
        }
      >
        <p>Ya existe un caso con este radicado y la misma actuación:</p>
        {dup.strict.map(s => (
          <div key={s.id} className="card tight" style={{ marginTop: 8 }}>
            <div style={{ fontWeight: 600 }}>{s.case_number}</div>
            <div className="muted" style={{ fontSize: 12 }}>{s.accionante_nombre} · {TIPO_LABEL[s.tipo] || s.tipo}</div>
          </div>
        ))}
      </Modal>
    </form>
  );
}

function Flags({ state }) {
  const { flag_dp, setDp, flag_mp, setMp, flag_vn, setVn, flag_na, setNa, entidad_na, setEntidad, forzar, setForzar, autorizadoPor, setAutorizadoPor, allUsers } = state;
  return (
    <div style={{ marginTop: 16 }}>
      <div className="label-uc" style={{ marginBottom: 8 }}>Banderas</div>
      <div className="row" style={{ gap: 16, flexWrap: "wrap" }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 0, textTransform: "none", fontSize: 13, color: "var(--text-body)", letterSpacing: 0 }}>
          <input type="checkbox" checked={flag_dp} onChange={(e) => setDp(e.target.checked)} /> Derecho de petición (DP)
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 0, textTransform: "none", fontSize: 13, color: "var(--text-body)", letterSpacing: 0 }}>
          <input type="checkbox" checked={flag_mp} onChange={(e) => setMp(e.target.checked)} /> Medida provisional (MP)
        </label>
        {/* feature/nulidad-bandera — sprint86. */}
        <label title="Marca esta opción cuando la tutela llega nuevamente después de que se decretó nulidad sobre la radicación anterior."
               style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 0, textTransform: "none", fontSize: 13, color: "var(--text-body)", letterSpacing: 0 }}>
          <input type="checkbox" checked={flag_vn} onChange={(e) => setVn(e.target.checked)} /> Vuelve después de nulidad
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 0, textTransform: "none", fontSize: 13, color: "var(--text-body)", letterSpacing: 0 }}>
          <input type="checkbox" checked={flag_na} onChange={(e) => setNa(e.target.checked)} /> Accionado N/A (no es Previsora)
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 0, textTransform: "none", fontSize: 13, color: "var(--text-body)", letterSpacing: 0 }}>
          <input type="checkbox" checked={forzar} onChange={(e) => setForzar(e.target.checked)} /> Forzar insumo (override)
        </label>
      </div>

      {flag_na && (
        <div className="grid-2" style={{ marginTop: 8 }}>
          <div>
            <label>Entidad N/A</label>
            <select value={entidad_na} onChange={(e) => setEntidad(e.target.value)} required>
              <option value="">— elegir —</option>
              {["FIDUPREVISORA","FOMAG","INPEC","EPS","OTRA"].map(x => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
        </div>
      )}

      {forzar && (
        <div className="grid-2" style={{ marginTop: 8 }}>
          <div>
            <label>Autorizado por</label>
            <select value={autorizadoPor} onChange={(e) => setAutorizadoPor(e.target.value)} required>
              <option value="">— elegir —</option>
              {allUsers.filter(u => ["admin","reparto"].includes(u.role)).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
